import React, { useEffect, useRef, useState } from 'react'
import { X, Send, MessageCircle } from 'lucide-react'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'
import toast from 'react-hot-toast'

const INTRO_MESSAGE = {
  id: 'intro',
  senderType: 'ai',
  content: "Hi! I'm Ink, Inkcredible Studios' assistant 👋 How can I help you today? I can answer questions about your subscription, job status, or how the portal works.",
  createdAt: new Date().toISOString()
}

export default function SupportWidget() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [showNeedHelp, setShowNeedHelp] = useState(false)
  const [messages, setMessages] = useState<any[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [escalated, setEscalated] = useState(false)
  const [queryMode, setQueryMode] = useState(false)
  const [queryText, setQueryText] = useState('')
  const [querySubmitted, setQuerySubmitted] = useState(false)
  const [showQueryButton, setShowQueryButton] = useState(false)
  const [improvingQuery, setImprovingQuery] = useState(false)
  const [unread, setUnread] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const userScrolledUp = useRef(false)
  const prevMessageCountRef = useRef(0)
  const pollRef = useRef<NodeJS.Timeout | null>(null)
  const initialScrollDone = useRef(false)

  useEffect(() => {
    if (open) initialScrollDone.current = false
  }, [open])

  const isClient = user?.roleName === 'client'
  const [chatEnabled, setChatEnabled] = useState(true)

  // Remove this effect entirely — we handle scrolling manually

  const loadConversation = async (isInitial = false) => {
    if (!user || user.roleName !== 'client') return
    try {
      const res = await api.get('/support/conversation')
      setConversationId(res.data.id)
      if (res.data.messages.length === 0) {
        if (isInitial) {
          setMessages([INTRO_MESSAGE])
          setEscalated(false)
        }
      } else {
        const newMessages = [INTRO_MESSAGE, ...res.data.messages]
        // Only update if new messages arrived or initial load
        setMessages(prev => {
          if (isInitial || newMessages.length > prev.length) {
            if (isInitial) {
              // Smooth scroll to bottom on initial load
              setTimeout(() => {
                if (!initialScrollDone.current && scrollContainerRef.current) {
                  initialScrollDone.current = true
                  scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' })
                }
              }, 150)
            } else if (!userScrolledUp.current) {
              // New message arrived and user is near bottom — scroll smoothly
              setTimeout(() => {
                if (scrollContainerRef.current) {
                  const container = scrollContainerRef.current
                  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight
                  if (distanceFromBottom < 150) {
                    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
                  }
                }
              }, 100)
            }
            return newMessages
          }
          return prev
        })
        const unreadStaff = res.data.messages.filter((m: any) => m.senderType === 'staff' && !m.isRead).length
        setUnread(unreadStaff)
        const hasStaffReply = res.data.messages.some((m: any) => m.senderType === 'staff')
        if (hasStaffReply) setEscalated(false)
      }
    } catch {}
  }

  useEffect(() => {
    if (open) {
      setMessages([INTRO_MESSAGE])
      userScrolledUp.current = false
      loadConversation(true)
      setUnread(0)
      pollRef.current = setInterval(async () => {
        if (!user || user.roleName !== 'client') return
        try {
          const res = await api.get('/support/conversation')
          const newMsgs = [INTRO_MESSAGE, ...res.data.messages]
          setMessages(prev => {
            if (newMsgs.length > prev.length) {
              // Only scroll if new message is from staff (not client's own message)
              const lastNew = newMsgs[newMsgs.length - 1]
              const isNewStaffMsg = lastNew?.senderType === 'staff' || lastNew?.senderType === 'ai'
              return newMsgs
            }
            return prev
          })
          const unreadStaff = res.data.messages.filter((m: any) => m.senderType === 'staff' && !m.isRead).length
          setUnread(unreadStaff)
          const hasStaffReply = res.data.messages.some((m: any) => m.senderType === 'staff')
          if (hasStaffReply) setEscalated(false)
        } catch {}
      }, 30000)
    } else {
      if (pollRef.current) clearInterval(pollRef.current)
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [open])

  // Poll for unread when closed
  useEffect(() => {
    const poll = setInterval(async () => {
      if (!open && user?.roleName === 'client') {
        try {
          const res = await api.get('/support/conversation')
          const unreadStaff = res.data.messages.filter((m: any) => m.senderType === 'staff' && !m.isRead).length
          setUnread(unreadStaff)
        } catch {}
      }
    }, 30000)
    return () => clearInterval(poll)
  }, [open])

  const sendMessage = async () => {
    if (!input.trim() || sending) return
    const userMsg = { id: Date.now().toString(), senderType: 'client', content: input.trim(), createdAt: new Date().toISOString() }
    setMessages(prev => [...prev, userMsg])
    const text = input.trim()
    setInput('')
    setSending(true)
    userScrolledUp.current = false

    try {
      const res = await api.post('/support/message', { content: text })
      setMessages(prev => [...prev, res.data.aiMessage])
      userScrolledUp.current = false
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' })
        }
      })
      if (res.data.escalated) {
        setEscalated(true)
        setShowQueryButton(true)
        setQuerySubmitted(false)
      } else if (res.data.staffActive) {
        // Staff has taken over — show waiting message
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          senderType: 'ai',
          content: 'Our support team will respond to you shortly.',
          createdAt: new Date().toISOString()
        }])
      }
    } catch {
      setMessages(prev => [...prev, {
        id: Date.now().toString(), senderType: 'ai',
        content: 'Sorry, something went wrong. Please try again.',
        createdAt: new Date().toISOString()
      }])
    }
    setSending(false)
  }

  const submitQuery = async () => {
    if (!queryText.trim() || !conversationId) return
    try {
      await api.post('/support/query', { content: queryText, conversationId })
      setQuerySubmitted(true)
      setQueryMode(false)
      setQueryText('')
      setEscalated(false)
      toast.success('Your query has been submitted. We will notify you when we respond!', { duration: 4000 })
      setMessages(prev => [...prev, {
        id: Date.now().toString(), senderType: 'client',
        content: queryText, createdAt: new Date().toISOString()
      }, {
        id: (Date.now() + 1).toString(), senderType: 'ai',
        content: "✅ Your query has been submitted. Our team will get back to you soon!",
        createdAt: new Date().toISOString()
      }])
      setQueryMode(false)
      setQueryText('')
    } catch {}
  }

  const playSound = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain); gain.connect(ctx.destination)
      osc.frequency.setValueAtTime(880, ctx.currentTime)
      gain.gain.setValueAtTime(0.3, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3)
      osc.start(); osc.stop(ctx.currentTime + 0.3)
    } catch {}
  }

  useEffect(() => {
    if (unread > 0 && !open) playSound()
  }, [unread])

  useEffect(() => {
    api.get('/settings/public/client_support_chat_enabled')
      .then(res => setChatEnabled(res.data.value !== 'false'))
      .catch(() => {})
  }, [])

  // Auto open on desktop after 3 seconds, show 'Need help' bubble on mobile
  useEffect(() => {
    if (!chatEnabled) return
    const isMobile = window.innerWidth < 640
    const timer = setTimeout(() => {
      if (isMobile) {
        setShowNeedHelp(true)
      } else {
        setOpen(true)
        setMessages([INTRO_MESSAGE])
      }
    }, 3000)
    return () => clearTimeout(timer)
  }, [chatEnabled])

  if (!isClient || !chatEnabled) return null

  return (
    <>
      {/* Chat bubble */}
      <div className="fixed bottom-24 sm:bottom-6 right-6 z-50">
        {showNeedHelp && !open && (
          <div className="absolute -top-10 right-0 sm:hidden">
            <button onClick={() => { setOpen(true); setShowNeedHelp(false) }}
              className="bg-white dark:bg-[#1a3050] text-navy-800 dark:text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg border border-gray-100 dark:border-[#1e3a5f] whitespace-nowrap animate-bounce">
              Need help? 👋
            </button>
          </div>
        )}
        <button onClick={() => { setOpen(v => !v); setShowNeedHelp(false) }}
          className="w-14 h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105">
          {open ? <X size={22} /> : <MessageCircle size={22} />}
        </button>
        {unread > 0 && !open && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unread}
          </span>
        )}
      </div>

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-40 sm:bottom-24 right-2 sm:right-6 z-50 w-[calc(100vw-16px)] sm:w-96 h-[70vh] sm:h-[500px] bg-white dark:bg-[#0d1e35] rounded-2xl shadow-2xl border border-gray-100 dark:border-[#1e3a5f] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-[#071829] px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-sm">I</div>
            <div>
              <p className="text-white font-semibold text-sm">Ink — Support</p>
              <p className="text-orange-300 text-xs">Inkcredible Studios Assistant</p>
            </div>
            <button onClick={() => setOpen(false)} className="ml-auto text-gray-400 hover:text-white">
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3"
            onScroll={() => {
              const container = scrollContainerRef.current
              if (!container) return
              const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150
              userScrolledUp.current = !isNearBottom
            }}>
            {messages.map((m, i) => {
              // Find if previous message was from client (for reply threading)
              const prevMsg = i > 0 ? messages[i - 1] : null
              const isStaffReply = m.senderType === 'staff'
              const prevClientMsg = isStaffReply && prevMsg?.senderType === 'client' ? prevMsg : null

              return (
                <div key={m.id || i} className={`flex flex-col ${m.senderType === 'client' ? 'items-end' : 'items-start'}`}>
                  {/* Show quoted client message for staff replies */}
                  {isStaffReply && prevClientMsg && (
                    <div className="max-w-[80%] px-3 py-1.5 mb-1 bg-gray-50 dark:bg-[#0d1e35] border-l-2 border-blue-300 rounded-lg text-xs text-gray-400 dark:text-gray-500 italic">
                      "{prevClientMsg.content.slice(0, 80)}{prevClientMsg.content.length > 80 ? '...' : ''}"
                    </div>
                  )}
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                    m.senderType === 'client'
                      ? 'bg-orange-500 text-white rounded-br-sm'
                      : m.senderType === 'staff'
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-200 rounded-bl-sm border border-blue-100 dark:border-blue-700/30'
                      : 'bg-gray-100 dark:bg-[#1a3050] text-navy-800 dark:text-gray-200 rounded-bl-sm'
                  }`}>
                    {m.senderType === 'staff' && (
                      <p className="text-xs font-semibold text-blue-500 mb-1">Support Team</p>
                    )}
                    {m.content}
                  </div>
                </div>
              )
            })}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-[#1a3050] px-3 py-2 rounded-2xl rounded-bl-sm">
                  <span className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Query mode */}
          {showQueryButton && (
            <div className="px-4 pb-2">
              {queryMode ? (
                <div className="space-y-2">
                  <textarea rows={3} className="w-full border border-gray-200 dark:border-[#1e3a5f] rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-orange-400 dark:bg-[#1a3050] dark:text-white"
                    placeholder="Describe your query in detail..."
                    value={queryText} onChange={e => setQueryText(e.target.value)} />
                  <div className="flex gap-2">
                    <button onClick={() => setQueryMode(false)} className="text-xs text-gray-400 hover:text-gray-600 py-1.5">Cancel</button>
                    <button onClick={submitQuery} disabled={!queryText.trim()}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-medium py-1.5 rounded-lg">
                      Submit Query
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setQueryMode(true); setQuerySubmitted(false); setShowQueryButton(true) }}
                  className="w-full border border-dashed border-orange-300 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/10 text-xs font-medium py-2 rounded-xl transition-colors">
                  📩 {querySubmitted ? 'Submit another query' : 'Leave a query for our team'}
                </button>
              )}
            </div>
          )}

          {/* Input */}
          {!escalated && (
            <div className="px-4 py-3 border-t border-gray-100 dark:border-[#1e3a5f]">
              <div className="flex items-end gap-2">
                <textarea rows={1} className="flex-1 border border-gray-200 dark:border-[#1e3a5f] rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:border-orange-400 dark:bg-[#1a3050] dark:text-white"
                  placeholder="Type a message..."
                  value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage() } }} />
                <button onClick={sendMessage} disabled={!input.trim() || sending}
                  className="w-9 h-9 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0">
                  <Send size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}